export const AUTH_LOGOUT_STORAGE_KEY = 'bci-auth-logout';
export const AUTH_LOGOUT_CHANNEL_NAME = 'bci-auth-sync';

/**
 * Notify other browser tabs that the user signed out (ECU-2993).
 * storage events fire in other tabs only; BroadcastChannel covers split-view tabs.
 */
export function broadcastAuthLogout(): void {
  try {
    window.localStorage.setItem(AUTH_LOGOUT_STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore quota / private-mode failures; BroadcastChannel may still notify peers.
  }

  if (typeof BroadcastChannel === 'undefined') {
    return;
  }

  const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL_NAME);
  channel.postMessage('logout');
  channel.close();
}

/** True when another tab (or this tab) recorded a sign-out that has not been followed by a new login. */
export function hasAuthLogoutMarker(): boolean {
  try {
    return Boolean(window.localStorage.getItem(AUTH_LOGOUT_STORAGE_KEY));
  } catch {
    return false;
  }
}

/** Clear the sign-out marker after a successful login so other tabs can restore the session. */
export function clearAuthLogoutMarker(): void {
  try {
    window.localStorage.removeItem(AUTH_LOGOUT_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Whether MSAL accounts should restore React auth state.
 * After a cross-tab logout the in-memory MSAL instance can still report accounts
 * until the cache reloads, so a logout marker must win over getAllAccounts().
 */
export function shouldRestoreMsalSession(hasLogoutMarker: boolean, accountCount: number): boolean {
  return !hasLogoutMarker && accountCount > 0;
}

/**
 * Subscribe to sign-out from another tab. Returns an unsubscribe function.
 */
export function subscribeToAuthLogout(onLogout: () => void): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key === AUTH_LOGOUT_STORAGE_KEY && event.newValue) {
      onLogout();
    }
  };
  window.addEventListener('storage', onStorage);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>): void => {
      if (event.data === 'logout') {
        onLogout();
      }
    };
  }

  return (): void => {
    window.removeEventListener('storage', onStorage);
    channel?.close();
  };
}
